import { trackEvent } from "@/lib/track/track-event";
import { TrackingEvent } from "@/lib/track/track-event";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

type MusicStatus = "playing" | "paused" | "ready";

interface MusicControlsContext {
  status: MusicStatus;
  currentMusicAssetId: string | null;
  controls: {
    play: (assetId: string) => void;
    toggle: () => void;
    pause: () => void;
    finish: () => void;
  };
}

const MusicControlsContext = createContext<MusicControlsContext | undefined>(undefined);

export function MusicControlsProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<MusicStatus>("ready");
  const [currentMusicAssetId, setCurrentMusicAssetId] = useState<string | null>(null);

  const pause = useCallback(() => {
    setStatus("paused");
  }, []);

  const play = useCallback(
    (assetId: string) => {
      if (currentMusicAssetId !== assetId) {
        setCurrentMusicAssetId(assetId);
      }
      setStatus("playing");
    },
    [currentMusicAssetId],
  );

  const toggle = useCallback(() => {
    setStatus((prev) => (prev === "playing" ? "paused" : "playing"));
  }, []);

  const finish = useCallback(() => {
    setCurrentMusicAssetId(null);
  }, []);

  const controls = useMemo(() => ({ play, toggle, pause, finish }), [play, toggle, pause, finish]);

  useEffect(() => {
    if (status === "playing") {
      trackEvent(TrackingEvent.PlayMusic, {
        music_id: currentMusicAssetId,
      });
    } else if (status === "paused") {
      trackEvent(TrackingEvent.StopMusic, {
        music_id: currentMusicAssetId,
      });
    }
  }, [status, currentMusicAssetId]);

  return (
    <MusicControlsContext.Provider value={{ status, currentMusicAssetId, controls }}>
      {children}
    </MusicControlsContext.Provider>
  );
}

export function useMusicControls() {
  const context = useContext(MusicControlsContext);

  if (!context) {
    throw new Error("useMusicControls must be used within a MusicControlsProvider");
  }

  return context;
}
